import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, Trash2, UploadCloud } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch } from "../lib/api";
import { parseSchedulingFile, persistImport, type ImportProgress } from "../lib/importer";
import type { ImportRecord, ParseResult } from "../shared/schemas";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/ui";

export function ImportsPage() {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [preview, setPreview] = useState<ParseResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  const importsQuery = useQuery({
    queryKey: ["imports"],
    queryFn: () => apiFetch<ImportRecord[]>("/imports"),
  });
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      setProgress({ phase: "parsing", percent: 2, message: "Parsing and validating in a Web Worker..." });
      const result = await parseSchedulingFile(file);
      setPreview(result);
      if (result.sections.length === 0 || result.terms.length === 0) {
        throw new Error("No importable active sections were found. Review validation errors.");
      }
      return persistImport(file, displayName.trim() || file.name, result, setProgress);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["imports"] }),
        queryClient.invalidateQueries({ queryKey: ["terms"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/imports/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["imports"] }),
  });
  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch(`/imports/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ displayName: name }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["imports"] }),
        queryClient.invalidateQueries({ queryKey: ["terms"] }),
      ]);
    },
  });

  if (importsQuery.isLoading) return <LoadingState />;
  if (importsQuery.error) return <ErrorState error={importsQuery.error} />;
  const canImport = user.role === "administrator" || user.role === "scheduler";

  return (
    <>
      <PageHeader
        title="Import History"
        description="CSV, XLS, and XLSX reports are validated in-browser, archived in R2, normalized into D1, and activated per term."
        actions={canImport ? (
          <>
            <input ref={fileInput} className="hidden" type="file" accept=".csv,.xls,.xlsx" onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              setSelectedFile(file);
              setDisplayName(file?.name.replace(/\.[^.]+$/, "") ?? "");
              setPreview(null);
              setProgress(null);
              importMutation.reset();
            }} />
            <button className="button-primary" onClick={() => fileInput.current?.click()}><UploadCloud className="h-4 w-4" /> Select report</button>
          </>
        ) : undefined}
      />
      {selectedFile && canImport && (
        <section className="panel p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-brand-50 p-3 text-brand-600"><FileSpreadsheet className="h-5 w-5" /></div>
              <div><p className="font-bold text-slate-900">{selectedFile.name}</p><p className="text-xs text-slate-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p></div>
            </div>
            <button className="button-primary" disabled={importMutation.isPending || progress?.phase === "complete"} onClick={() => importMutation.mutate(selectedFile)}>
              {importMutation.isPending ? "Importing..." : "Parse and import"}
            </button>
          </div>
          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Import display name</span>
            <input
              className="field w-full max-w-xl"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Example: Fall 2026 initial Cognos export"
            />
          </label>
          {progress && (
            <div className="mt-5">
              <div className="mb-2 flex justify-between text-xs font-semibold text-slate-600"><span>{progress.message}</span><span>{progress.percent}%</span></div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-brand-600 transition-all" style={{ width: `${progress.percent}%` }} /></div>
            </div>
          )}
          {importMutation.error && <p className="mt-4 rounded-lg bg-rose-50 p-3 text-sm font-medium text-rose-700">{importMutation.error.message}</p>}
          {preview && (
            <div className="mt-5 grid gap-3 sm:grid-cols-5">
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-[10px] font-bold uppercase text-slate-400">Source rows</p><p className="mt-1 font-black">{preview.sourceRowCount}</p></div>
              <div className="rounded-lg bg-emerald-50 p-3"><p className="text-[10px] font-bold uppercase text-emerald-600">Included</p><p className="mt-1 font-black">{preview.includedRowCount}</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-[10px] font-bold uppercase text-slate-400">Excluded</p><p className="mt-1 font-black">{preview.excludedRowCount}</p></div>
              <div className="rounded-lg bg-amber-50 p-3"><p className="text-[10px] font-bold uppercase text-amber-600">Warnings</p><p className="mt-1 font-black">{preview.issues.filter((issue) => issue.severity === "warning").length}</p></div>
              <div className="rounded-lg bg-rose-50 p-3"><p className="text-[10px] font-bold uppercase text-rose-600">Errors</p><p className="mt-1 font-black">{preview.issues.filter((issue) => issue.severity === "error").length}</p></div>
              <p className="sm:col-span-5 text-xs text-slate-500">Parsed in {preview.elapsedMs.toFixed(0)} ms · Terms {preview.terms.join(", ")}</p>
            </div>
          )}
        </section>
      )}
      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">File</th><th className="px-4 py-3">Terms</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Rows</th><th className="px-4 py-3">Issues</th><th className="px-4 py-3">Uploaded</th><th className="px-4 py-3">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {importsQuery.data?.map((record) => (
                <tr key={record.id}>
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900">{record.displayName}</p>
                    <p className="text-xs text-slate-400">{record.fileName} Â· {(record.byteSize / 1024 / 1024).toFixed(2)} MB</p>
                    {canImport && (
                      <div className="mt-2 flex gap-2">
                        <input
                          className="field h-9 min-w-0"
                          value={renameDrafts[record.id] ?? record.displayName}
                          onChange={(event) =>
                            setRenameDrafts((current) => ({
                              ...current,
                              [record.id]: event.target.value,
                            }))
                          }
                          aria-label={`Rename ${record.displayName}`}
                        />
                        <button
                          className="button-secondary px-3"
                          type="button"
                          disabled={renameMutation.isPending}
                          onClick={() =>
                            renameMutation.mutate({
                              id: record.id,
                              name: (renameDrafts[record.id] ?? record.displayName).trim(),
                            })
                          }
                        >
                          Save
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{record.terms.map((term) => <span key={term.id} className={`mr-1 badge ${term.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{term.termCode}</span>)}</td>
                  <td className="px-4 py-3"><StatusBadge status={record.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{record.includedRows.toLocaleString()} included<br /><span className="text-xs text-slate-400">{record.excludedRows.toLocaleString()} excluded</span></td>
                  <td className="px-4 py-3 text-slate-600">{record.warningCount} warnings<br /><span className="text-xs text-rose-500">{record.errorCount} errors</span></td>
                  <td className="px-4 py-3 text-slate-500">{new Date(record.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <a className="button-secondary px-3" href={`/api/imports/${record.id}/source`} aria-label={`Download ${record.fileName}`}><Download className="h-4 w-4" /></a>
                      {user.role === "administrator" && <button className="button-secondary px-3 text-rose-600" onClick={() => { if (confirm(`Delete ${record.fileName} and its snapshots?`)) deleteMutation.mutate(record.id); }} aria-label={`Delete ${record.fileName}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
